import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { SimpleMenuSection } from "@repo/ui/Menu/simpleMenuSection";
import { IconExternalLink, IconSelector } from "@tabler/icons-react";
import type { FC } from "react";
import {
  Button as AriaButton,
  MenuTrigger,
  type Placement,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

interface UserProfileMenuItemBase {
  key: string;
  label: string;
}

/** Menu entry that navigates to a route. */
export interface UserProfileNavigationItem extends UserProfileMenuItemBase {
  href: string;
  /** Opens the link in a new tab and marks it with an external-link icon. */
  opensInNewWindow?: boolean;
}

/** Menu entry that runs a handler instead of navigating. */
export interface UserProfileActionItem extends UserProfileMenuItemBase {
  onAction: () => void;
}

export type UserProfileMenuItem =
  | UserProfileNavigationItem
  | UserProfileActionItem;

/** The service access session the profile item stands for. */
export interface UserProfileServiceAccess {
  companyName: string;
  workspaceAlias: string;
  /** The time left until the session ends, such as "5 h 42 min left". */
  timeLeft: string;
  /** Whether the session ends soon, which shows in the warning color. */
  isEndingSoon: boolean;
  onEnd: () => void;
}

export interface UserProfileWidgetProps {
  name: string;
  email: string;
  menuItems: readonly UserProfileMenuItem[];
  /** Where the menu opens relative to the trigger. Defaults to the sidebar-friendly "top start". */
  menuPlacement?: Placement;
  /** Set in a service access session, which then shows in place of the email. */
  serviceAccess?: UserProfileServiceAccess;
}

export const UserProfileWidget: FC<UserProfileWidgetProps> = ({
  name,
  email,
  menuItems,
  menuPlacement = "top start",
  serviceAccess,
}) => (
  <MenuTrigger>
    <AriaButton
      aria-label={
        serviceAccess
          ? `Open user menu, service access to ${serviceAccess.companyName} (${serviceAccess.workspaceAlias})`
          : "Open user menu"
      }
      className={twMerge(
        "flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left",
        "hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        serviceAccess?.isEndingSoon && "bg-amber-100 hover:bg-amber-200",
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        {serviceAccess ? (
          <span
            className={twMerge(
              "mb-1 w-fit rounded-full bg-slate-900 px-2 py-0.5 text-xs font-medium text-white",
              serviceAccess.isEndingSoon && "bg-amber-600",
            )}
          >
            Service access
          </span>
        ) : null}
        <span className="truncate text-sm font-semibold text-sidebar-foreground">
          {name}
        </span>
        {serviceAccess ? (
          <>
            <span className="truncate text-xs text-foreground">
              {serviceAccess.companyName}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {serviceAccess.workspaceAlias}
            </span>
          </>
        ) : (
          <span className="truncate text-xs text-foreground">{email}</span>
        )}
      </span>
      <IconSelector
        aria-hidden
        className="size-4 shrink-0 text-sidebar-foreground"
      />
    </AriaButton>
    <SimpleMenuPopover placement={menuPlacement}>
      <SimpleMenu aria-label="User menu">
        {menuItems.map((item) =>
          "href" in item ? (
            <SimpleMenuItem
              key={item.key}
              href={item.href}
              target={item.opensInNewWindow ? "_blank" : undefined}
              rel={item.opensInNewWindow ? "noreferrer" : undefined}
              textValue={item.opensInNewWindow ? item.label : undefined}
            >
              {item.opensInNewWindow ? (
                <>
                  <span className="min-w-0 flex-1 truncate" title={item.label}>
                    {item.label}
                  </span>
                  <IconExternalLink
                    aria-hidden
                    className="size-4 shrink-0 text-muted-foreground"
                  />
                </>
              ) : (
                item.label
              )}
            </SimpleMenuItem>
          ) : (
            <SimpleMenuItem key={item.key} onAction={item.onAction}>
              {item.label}
            </SimpleMenuItem>
          ),
        )}
        {serviceAccess ? (
          <SimpleMenuSection label={serviceAccess.timeLeft}>
            <SimpleMenuItem onAction={serviceAccess.onEnd}>
              Sign out
            </SimpleMenuItem>
          </SimpleMenuSection>
        ) : null}
      </SimpleMenu>
    </SimpleMenuPopover>
  </MenuTrigger>
);
